import { 
  collection, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase.config';
import { getCollectionForCompany } from './dataAdapter';
import { getShareDomain } from './domainConfig';

// 📧 Employee Invite Management System
export class EmployeeInviteManager {
  
  // Generate unique invite token
  static generateInviteToken() {
    return 'inv_' + Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
  }

  // Create employee invitation
  static async createEmployeeInvite(companyData, inviteData) {
    try {
      console.log('🔄 Creating employee invite:', { companyData, inviteData });
      const inviteToken = this.generateInviteToken();
      console.log('✅ Generated invite token:', inviteToken);
      
      const inviteRecord = {
        // Invite details
        inviteToken,
        email: inviteData.email,
        
        // Employee reference (from Employee Management)
        employeeId: inviteData.employeeId,
        globalEmployeeId: inviteData.globalEmployeeId,
        name: inviteData.name || '',
        designation: inviteData.designation || '',
        department: inviteData.department || '',
        role: inviteData.role || 'employee',
        
        // Company details
        companyId: companyData.companyId,
        companyName: companyData.firmName || companyData.name || '',
        invitedBy: inviteData.invitedBy || '',
        
        // Status tracking
        status: 'invited', // invited, joined, expired, rejected
        invitedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)), // 7 days
        
        // Invite link (always use production domain)
        inviteLink: `${getShareDomain()}/invite/${inviteToken}`,
        
        // Additional data
        message: inviteData.message || '',
        permissions: inviteData.permissions || {},
        
        // Tracking
        emailSent: false,
        remindersSent: 0,
        lastReminderAt: null
      };
      
      console.log('📝 Creating invite record:', inviteRecord);
      
      const inviteRef = await addDoc(
        getCollectionForCompany(db, companyData, 'employee_invites'),
        inviteRecord
      );
      
      console.log('✅ Invite created with ID:', inviteRef.id);

      // Update employee status to show they have been invited
      if (inviteData.employeeId) {
        try {
          const employeeRef = doc(getCollectionForCompany(db, companyData, 'employees'), inviteData.employeeId);
          await updateDoc(employeeRef, {
            inviteStatus: 'invited',
            inviteToken: inviteToken,
            invitedAt: serverTimestamp(),
            email: inviteData.email
          });
        } catch (employeeUpdateError) {
          console.error('Error updating employee invite status:', employeeUpdateError);
          // Don't fail the invite creation if employee update fails
        }
      }
      
      return { 
        success: true, 
        inviteId: inviteRef.id, 
        inviteToken,
        inviteLink: inviteRecord.inviteLink
      };
    } catch (error) {
      console.error('Error creating employee invite:', error);
      return { success: false, error: error.message };
    }
  }

  // Get invite details by token
  static async getInviteByToken(inviteToken) {
    try {
      // Search across all companies for this invite token
      const companiesRef = collection(db, 'companies');
      const companiesSnap = await getDocs(companiesRef);
      
      for (const companyDoc of companiesSnap.docs) {
        const companyId = companyDoc.id;
        
        const invitesQuery = query(
          collection(db, `companies/${companyId}/employee_invites`),
          where('inviteToken', '==', inviteToken)
        );
        
        const invitesSnap = await getDocs(invitesQuery);
        
        if (!invitesSnap.empty) {
          const inviteDoc = invitesSnap.docs[0];
          const inviteData = { id: inviteDoc.id, ...inviteDoc.data() };
          
          // Get company details
          const companyData = { id: companyDoc.id, ...companyDoc.data() };
          
          return { 
            success: true, 
            invite: inviteData, 
            company: companyData 
          };
        }
      }
      
      return { success: false, error: 'Invite not found or expired' };
    } catch (error) {
      console.error('Error getting invite by token:', error);
      return { success: false, error: error.message };
    }
  }

  // Accept employee invitation
  static async acceptInvite(inviteToken, userId, userData) {
    try {
      // Get invite details
      const inviteResult = await this.getInviteByToken(inviteToken);
      
      if (!inviteResult.success) {
        return { success: false, error: 'Invalid or expired invite' };
      }
      
      const { invite, company } = inviteResult;
      
      // Check if invite is still valid
      const now = new Date();
      const expiresAt = invite.expiresAt?.toDate?.() || new Date(invite.expiresAt);
      
      if (now > expiresAt) {
        return { success: false, error: 'Invite has expired' };
      }
      
      if (invite.status !== 'invited') {
        return { success: false, error: 'Invite is no longer valid' };
      }
      
      // Update existing employee record with user information
      const employeeUpdateData = {
        // Link to Firebase Auth user
        userId: userId,
        
        // Update contact info if provided
        email: userData.email || invite.email || '',
        phone: userData.phone || '',
        
        // Invite tracking
        inviteStatus: 'joined',
        joinedAt: serverTimestamp(),
        inviteToken: inviteToken,
        
        // System status
        status: 'active'
      };
      
      // Update the existing employee record
      const employeeRef = doc(
        getCollectionForCompany(db, { companyId: company.id }, 'employees'), 
        invite.employeeId
      );
      await updateDoc(employeeRef, employeeUpdateData);
      
      // Update invite status
      const inviteRef = doc(db, `companies/${company.id}/employee_invites/${invite.id}`);
      await updateDoc(inviteRef, {
        status: 'joined',
        joinedAt: serverTimestamp(),
        joinedBy: userId,
        employeeId: invite.employeeId
      });
      
      // Add user-company relationship
      await this.createUserCompanyRelation(userId, company.id, 'employee');
      
      return { 
        success: true, 
        employeeId: invite.employeeId,
        companyId: company.id,
        companyName: company.firmName || company.name
      };
    } catch (error) {
      console.error('Error accepting invite:', error);
      return { success: false, error: error.message };
    }
  }

  // Get company invites with status
  static async getCompanyInvites(companyData, filters = {}) {
    try {
      let invitesQuery = collection(db, `companies/${companyData.companyId}/employee_invites`);
      
      const constraints = [];
      
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      
      if (constraints.length > 0) {
        invitesQuery = query(invitesQuery, ...constraints);
      }
      
      const invitesSnap = await getDocs(invitesQuery);
      const invites = [];
      
      invitesSnap.forEach(doc => {
        const data = doc.data();
        invites.push({ 
          id: doc.id, 
          ...data,
          expiresAt: data.expiresAt?.toDate?.() || new Date(data.expiresAt),
          invitedAt: data.invitedAt?.toDate?.() || new Date(data.invitedAt),
          isExpired: new Date() > (data.expiresAt?.toDate?.() || new Date(data.expiresAt))
        });
      });
      
      // Sort by invitation date (newest first)
      invites.sort((a, b) => b.invitedAt - a.invitedAt);
      
      return { success: true, data: invites };
    } catch (error) {
      console.error('Error fetching company invites:', error);
      return { success: false, error: error.message };
    }
  }

  // Resend/Update invitation
  static async resendInvite(companyData, inviteId, updateData = {}) {
    try {
      const inviteRef = doc(db, `companies/${companyData.companyId}/employee_invites/${inviteId}`);
      
      const updatePayload = {
        ...updateData,
        lastReminderAt: serverTimestamp(),
        remindersSent: updateData.remindersSent || 0,
        // Extend expiry by 7 days
        expiresAt: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000))
      };
      
      await updateDoc(inviteRef, updatePayload);
      
      return { success: true };
    } catch (error) {
      console.error('Error resending invite:', error);
      return { success: false, error: error.message };
    }
  }

  // Cancel invitation
  static async cancelInvite(companyData, inviteId, reason = '') {
    try {
      const inviteRef = doc(db, `companies/${companyData.companyId}/employee_invites/${inviteId}`);
      
      await updateDoc(inviteRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancellationReason: reason
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error cancelling invite:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper: Generate employee ID
  static async generateEmployeeId(companyId) {
    try {
      const employeesRef = collection(db, `companies/${companyId}/employees`);
      const employeesSnap = await getDocs(employeesRef);
      
      let maxId = 0;
      employeesSnap.forEach(doc => {
        const data = doc.data();
        const match = (data.employeeId || '').match(/^EMP(\d+)$/);
        if (match) {
          maxId = Math.max(maxId, parseInt(match[1], 10));
        }
      });
      
      return `EMP${String(maxId + 1).padStart(3, '0')}`;
    } catch (error) {
      return `EMP${String(Date.now()).slice(-3)}`;
    }
  }

  // Helper: Generate global employee ID
  static generateGlobalEmployeeId() {
    return 'GEMP_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);
  }

  // Helper: Create user-company relationship
  static async createUserCompanyRelation(userId, companyId, role) {
    try {
      const userCompaniesRef = doc(db, `user_companies/${userId}`);
      const userCompaniesDoc = await getDoc(userCompaniesRef);
      
      const existingData = userCompaniesDoc.exists() ? userCompaniesDoc.data() : {};
      
      // Use legacy structure for owner-employee relationship importance
      const ownedCompanies = existingData.owned_companies || [];
      const employedCompanies = existingData.employed_companies || [];
      
      // Determine which array to update based on role
      let targetArray, targetField;
      if (role === 'owner') {
        targetArray = ownedCompanies;
        targetField = 'owned_companies';
      } else {
        targetArray = employedCompanies;
        targetField = 'employed_companies';
      }
      
      // Check if relationship already exists
      const existingIndex = targetArray.findIndex(c => c.companyId === companyId);
      
      // Use manual ISO timestamp to avoid Firebase conflicts
      const now = new Date().toISOString();
      const companyRelation = {
        companyId,
        role,
        created_at: now,
        status: 'active'
      };
      
      if (existingIndex >= 0) {
        // Update existing relationship
        targetArray[existingIndex] = {
          ...targetArray[existingIndex],
          ...companyRelation
        };
      } else {
        // Add new relationship
        targetArray.push(companyRelation);
      }
      
      // Update the appropriate array
      const updateData = {
        [targetField]: targetArray,
        lastUpdated: new Date()
      };
      
      await updateDoc(userCompaniesRef, updateData);
      
      return { success: true };
    } catch (error) {
      console.error('Error creating user-company relation:', error);
      return { success: false, error: error.message };
    }
  }

  // Get invite statistics
  static async getInviteStats(companyData) {
    try {
      const invites = await this.getCompanyInvites(companyData);
      
      if (!invites.success) {
        return { success: false, error: invites.error };
      }
      
      const stats = {
        total: invites.data.length,
        invited: invites.data.filter(i => i.status === 'invited' && !i.isExpired).length,
        joined: invites.data.filter(i => i.status === 'joined').length,
        expired: invites.data.filter(i => i.isExpired && i.status === 'invited').length,
        cancelled: invites.data.filter(i => i.status === 'cancelled').length,
        pending: invites.data.filter(i => i.status === 'invited' && !i.isExpired).length
      };
      
      return { success: true, data: stats };
    } catch (error) {
      console.error('Error getting invite stats:', error);
      return { success: false, error: error.message };
    }
  }
}

export default EmployeeInviteManager;
